import type { BoundRefusal } from '@/lib/variable-pill';
import { boundRefusalSentence } from '@/lib/variable-pill';
import type {
  ProtocolRangement,
  ProtocolRangementProblem,
  ProtocolStepDraft,
  RangerSystemPartKind,
} from '@matter_extractor/shared';
import { PROTOCOL_RANGEMENT_CONSIGNE_MAX, rangementTargetsKept } from '@matter_extractor/shared';

/**
 * Ce que la vue « Rangement » de l'éditeur de protocole dit et trie
 * (scenario-succes.md §4, issue #177) - les mots et les tris purs, sans un
 * seul composant, pour la raison que donne `analyst-reading.ts` : un écran se
 * relit mieux quand ses phrases sont testables sans rendu.
 *
 * What is NOT here is the assembly of the prompt: the panel renders
 * `rangerSystemParts(rangerPromptInput(…))` of `shared`, the functions the
 * server serves the rangeur with, and a second spelling of either in this
 * package would be exactly the divergence that descent exists to forbid.
 */

/* ------------------------------------------------- the targets, by step */

/** One objective of a step, as the list of targets offers it. */
export type TargetOption = {
  readonly id: string;
  /** The objective's name as typed - empty while a draft objective is not named yet. */
  readonly name: string;
  readonly checked: boolean;
};

/** One step's objectives, under the step's name. */
export type TargetGroup = {
  readonly stepId: string;
  readonly title: string;
  readonly options: readonly TargetOption[];
};

/**
 * EVERY objective of the protocol, grouped by step, in the order of the
 * protocol (§4, the maquette's « groupées par étape »): a step with no
 * objective draws no group. A step without a name is « Étape N », as the
 * reading's palette titles it. The ticks are the section's own list - a target
 * the steps no longer carry has no line here, and leaves the list at the save.
 */
export function targetGroups(
  steps: readonly ProtocolStepDraft[],
  rangement: ProtocolRangement,
): TargetGroup[] {
  const ticked = new Set(rangement.targetObjectiveIds);
  const groups: TargetGroup[] = [];
  steps.forEach((step, stepIndex) => {
    if (step.objectives.length === 0) return;
    const name = step.name.trim();
    groups.push({
      stepId: step.id,
      title: name === '' ? `Étape ${stepIndex + 1}` : name,
      options: step.objectives.map((objective) => ({
        id: objective.id,
        name: objective.name,
        checked: ticked.has(objective.id),
      })),
    });
  });
  return groups;
}

/**
 * The section with one objective ticked or unticked. The list is rebuilt in the
 * order of the PROTOCOL and not of the ticking (`rangementTargetsKept`): the
 * prompt lists the targets in that order and the table of Sessions follows it,
 * so the draft never holds a list the save would reorder.
 */
export function toggledTarget(
  steps: readonly ProtocolStepDraft[],
  rangement: ProtocolRangement,
  objectiveId: string,
  checked: boolean,
): ProtocolRangement {
  const ids = rangement.targetObjectiveIds.filter((id) => id !== objectiveId);
  if (checked) ids.push(objectiveId);
  return rangementTargetsKept(steps, { ...rangement, targetObjectiveIds: ids });
}

/* ------------------------------------- the setting « un seul objectif » */

/** The label of the checkbox under the targets (relecture.md §12, maquette `retouches` section 5). */
export const RANGEMENT_ONE_PASSAGE_LABEL = 'Un passage ne sert qu’un objectif';

/**
 * Its line of help: what ticking changes for the ranger and for a review. The
 * sentence of the contract itself is shown by « Ce que le rangeur recevra », and
 * is written in code.
 */
export const RANGEMENT_ONE_PASSAGE_HELP =
  'Décochée, une phrase peut servir deux objectifs. Cochée, le rangeur range chaque passage dans un seul objectif, et une relecture ne range un passage dans deux cases qu’avec un « ? » sur les deux.';

/* ------------------------------------------------------ the panel of §4 */

/**
 * The heading of each part in the panel - interface words, and NOT the `##`
 * lines of the prompt (README of the maquettes). The conversation is not in the
 * system block (it is the one user message, `rangerConversationMessage`), but it
 * is part of what the rangeur receives, and the maquette draws it between the
 * consigne and the contract.
 */
export type RangementPartKind = RangerSystemPartKind | 'conversation';

export const RANGEMENT_PART_LABEL: Readonly<Record<RangementPartKind, string>> = {
  framing: 'Le cadre',
  targets: 'Les objectifs cibles',
  consigne: 'La consigne',
  conversation: 'La conversation',
  contract: 'Le contrat de sortie',
};

/** Who writes each part, said on its badge (the maquette `EditeurRangement`). */
export const RANGEMENT_PART_BADGE: Readonly<Record<RangementPartKind, string>> = {
  framing: 'en code',
  targets: 'des cases cochées',
  consigne: 'ce protocole',
  conversation: 'en code, servie à la fin de la session',
  contract: 'en code',
};

/**
 * What the conversation is, in one sentence: it is not text of the system, so
 * `shared` has no body for it. The three things it names are the ones
 * `rangerConversationMessage` serves.
 */
export const RANGEMENT_CONVERSATION_SENTENCE =
  'Tous les tours, du small talk à la validation : le message de l’interviewer, la réponse, le numéro du tour, le nom de son étape et l’issue de l’étape (réussie, ratée ou en cours).';

/** The parts that have no body to fold: the maquette draws them as a sentence. */
export const RANGEMENT_NO_TARGET_SENTENCE = 'Aucun objectif cible : pas de rangement.';
export const RANGEMENT_NO_CONSIGNE_SENTENCE =
  'Aucune consigne : le rangeur ne peut pas ranger la matière.';

/* ------------------------------------------------- the field and its line */

/** The consigne is bounded at 6000 characters of STORED text, tokens included (§4). */
export const RANGEMENT_CONSIGNE_MAX = PROTOCOL_RANGEMENT_CONSIGNE_MAX;

/**
 * What the palette says above its greyed lines: a citation weighs its token
 * against the bound, and one that would pass it is refused, never cut (the
 * reading's régime, `READING_PALETTE_FULL`).
 */
export const RANGEMENT_PALETTE_FULL = `Plus de place pour une citation : la consigne dépasserait ses ${RANGEMENT_CONSIGNE_MAX} caractères. Raccourcissez-la pour citer un objectif.`;

/** What the `<output>` under the field says when its bound refuses an input. */
export function rangementRefusalSentence(refusal: BoundRefusal): string {
  return boundRefusalSentence(refusal, RANGEMENT_CONSIGNE_MAX, 'la consigne');
}

/**
 * The yellow line of each refusal of the section, said under the field it
 * concerns (§4): the consigne is missing under the consigne, the targets are
 * missing under the list of targets. The pill « N problèmes » says them too,
 * in one line; these say what to DO.
 */
export const RANGEMENT_REFUSAL_LINE: Readonly<Record<ProtocolRangementProblem['kind'], string>> = {
  'rangement-consigne-missing':
    'Des objectifs cibles sont cochés : écrivez la consigne du rangeur, ou décochez-les. La version ne s’enregistre pas ainsi.',
  'rangement-targets-missing':
    'La consigne est écrite, mais aucun objectif n’est coché : cochez au moins une cible, ou videz la consigne. La version ne s’enregistre pas ainsi.',
};
